import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";

export default function TiltCard({
  children,
  className = "",
  maxTilt = 12,
  glare = true,
  scale = 1.02,
}) {
  const cardRef = useRef(null);
  const glareRef = useRef(null);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const isFinePointer = window.matchMedia("(pointer: fine)").matches;
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!isFinePointer || prefersReducedMotion) return;

    const rotX = gsap.quickTo(card, "rotateX", { duration: 0.5, ease: "power2.out" });
    const rotY = gsap.quickTo(card, "rotateY", { duration: 0.5, ease: "power2.out" });
    const scaleTo = gsap.quickTo(card, "scale", { duration: 0.5, ease: "power2.out" });

    const handleMouseMove = (e) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const normX = (x / rect.width - 0.5) * 2; // -1 to 1
      const normY = (y / rect.height - 0.5) * 2; // -1 to 1

      rotX(-normY * maxTilt);
      rotY(normX * maxTilt);
      scaleTo(scale);

      if (glare && glareRef.current) {
        const glareX = (x / rect.width) * 100;
        const glareY = (y / rect.height) * 100;
        glareRef.current.style.background = `radial-gradient(circle at ${glareX}% ${glareY}%, rgba(125, 211, 252, 0.22) 0%, transparent 60%)`;
        glareRef.current.style.opacity = "1";
      }
    };

    const handleMouseLeave = () => {
      rotX(0);
      rotY(0);
      scaleTo(1);
      if (glare && glareRef.current) {
        glareRef.current.style.opacity = "0";
      }
    };

    card.addEventListener("mousemove", handleMouseMove);
    card.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      card.removeEventListener("mousemove", handleMouseMove);
      card.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [maxTilt, glare, scale]);

  return (
    <div
      style={{ perspective: "1000px" }}
      className={`relative inline-block w-full ${className}`}
    >
      <div
        ref={cardRef}
        style={{ transformStyle: "preserve-3d" }}
        className="w-full h-full will-change-transform relative"
      >
        {children}

        {/* Dynamic Light Glare Overlay */}
        {glare && (
          <div
            ref={glareRef}
            className="absolute inset-0 pointer-events-none rounded-inherit opacity-0 transition-opacity duration-300 z-30"
          />
        )}
      </div>
    </div>
  );
}
